import React, { useEffect, useMemo, useState } from "react";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/min";
import mobilePhoneMetadata from "libphonenumber-js/metadata.mobile.json";

const DEFAULT_COUNTRY = "IN";
const PHONE_CONTACT_TYPES = new Set(["mobile", "whatsapp", "parent_mobile"]);
const REGION_DISPLAY_NAMES =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

const PHONE_COUNTRY_OPTIONS = buildPhoneCountryOptions();
const PHONE_COUNTRY_OPTION_BY_CODE = new Map(
  PHONE_COUNTRY_OPTIONS.map((option) => [option.country, option])
);
const PHONE_COUNTRY_MAX_LENGTH_CACHE = new Map();

export function isIndianPhoneContactType(type) {
  return PHONE_CONTACT_TYPES.has(String(type || "").trim().toLowerCase());
}

export function getIndianPhoneInputValue(value) {
  return parseStoredPhoneParts(value, DEFAULT_COUNTRY, { inferCountryFromValue: true }).nationalNumber;
}

export function validateIndianPhoneValue(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parts = parseStoredPhoneParts(rawValue, DEFAULT_COUNTRY, { inferCountryFromValue: true });
  if (!parts.nationalNumber) return "";

  const possibleLengths = getCountryNationalNumberPossibleLengths(parts.country);
  const isPossibleLength = possibleLengths.includes(parts.nationalNumber.length);
  return isPossibleLength ? "" : `Enter a ${formatPossibleLengthLabel(possibleLengths)} phone number for the selected country code.`;
}

export function normalizeIndianPhone(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parts = parseStoredPhoneParts(rawValue, DEFAULT_COUNTRY, { inferCountryFromValue: true });
  const parsed = parsePhoneNumberFromString(parts.display || rawValue, parts.country);
  if (parsed?.isValid()) {
    return String(parsed.number || "").replace(/^\+/, "");
  }

  const fallbackDigits = `${parts.callingCode}${parts.nationalNumber}`.replace(/\D/g, "");
  return fallbackDigits || rawValue.replace(/\D/g, "");
}

export function formatIndianPhoneDisplay(value) {
  return toStoredIndianPhoneValue(value);
}

export function toStoredIndianPhoneValue(value) {
  return toStoredPhoneValueForCountry(value, DEFAULT_COUNTRY);
}

export function toStoredPhoneValueForCountry(value, fallbackCountry = DEFAULT_COUNTRY) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parts = parseStoredPhoneParts(rawValue, fallbackCountry, { inferCountryFromValue: true });
  if (!parts.nationalNumber) return rawValue;

  const parsed = parsePhoneNumberFromString(parts.display || rawValue, parts.country);
  if (parsed?.isValid()) {
    return formatStoredPhoneValue(parsed.country || parts.country, parsed.nationalNumber || parts.nationalNumber);
  }

  return formatStoredPhoneValue(parts.country, parts.nationalNumber);
}

export function getContactDisplayValue(contact) {
  const type = String(contact?.contact_type || contact?.type || "").trim().toLowerCase();
  const value = contact?.contact_value || "";
  return isIndianPhoneContactType(type) ? formatIndianPhoneDisplay(value) : String(value || "").trim();
}

export function getContactValueFieldLabel(type) {
  const normalized = String(type || "").trim().toLowerCase();
  if (normalized === "email") return "Email";
  if (normalized === "whatsapp") return "WhatsApp Number";
  if (normalized === "parent_mobile") return "Parent Mobile Number";
  if (normalized === "mobile") return "Phone Number";
  if (normalized === "other") return "Other Contact";
  return "Value";
}

export function IndianPhoneInput({
  value,
  onChange,
  onCountryChange,
  inputStyle,
  selectStyle,
  wrapperStyle,
  selectWrapStyle,
  placeholder = "Phone number",
  showDigitCount = true,
}) {
  const inferredValue = useMemo(
    () => parseStoredPhoneParts(value, DEFAULT_COUNTRY, { inferCountryFromValue: true }),
    [value]
  );
  const [selectedCountry, setSelectedCountry] = useState(inferredValue.country);
  const parsedValue = useMemo(
    () => parseStoredPhoneParts(value, selectedCountry || DEFAULT_COUNTRY, { lockCountryToFallback: true }),
    [selectedCountry, value]
  );
  const [nationalNumber, setNationalNumber] = useState(parsedValue.nationalNumber);
  const maxNationalNumberLength = useMemo(
    () => getCountryNationalNumberMaxLength(selectedCountry || DEFAULT_COUNTRY),
    [selectedCountry]
  );
  const digitCountLabel = `${nationalNumber.length}/${maxNationalNumberLength} digits`;

  useEffect(() => {
    const rawValue = String(value || "").trim();
    if (!rawValue) return;
    if (rawValue.startsWith("+")) {
      const digits = rawValue.replace(/\D/g, "");
      const currentCallingCode = getCountryOption(selectedCountry).callingCode;
      const parsedPhone = parsePhoneNumberFromString(rawValue, DEFAULT_COUNTRY);
      if (!parsedPhone?.country && digits.startsWith(currentCallingCode)) return;
    }
    setSelectedCountry(inferredValue.country);
  }, [inferredValue.country, selectedCountry, value]);

  useEffect(() => {
    setNationalNumber(parsedValue.nationalNumber);
  }, [parsedValue.nationalNumber]);

  return (
    <div
      style={{
        display: "grid",
        gap: 6,
        ...wrapperStyle,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "140px 1fr",
          gap: 12,
          alignItems: "stretch",
        }}
      >
        <div style={{ position: "relative", ...selectWrapStyle }}>
          <select
            value={selectedCountry}
            style={{
              width: "100%",
              ...selectStyle,
            }}
            onChange={(event) => {
              const nextCountry = String(event.target.value || DEFAULT_COUNTRY);
              const nextMaxLength = getCountryNationalNumberMaxLength(nextCountry);
              const trimmedNationalNumber = String(nationalNumber || "").slice(0, nextMaxLength);
              const nextStoredValue = formatStoredPhoneValue(nextCountry, trimmedNationalNumber);
              setSelectedCountry(nextCountry);
              setNationalNumber(trimmedNationalNumber);
              onCountryChange?.(nextCountry);
              onChange?.(nextStoredValue);
            }}
          >
            {PHONE_COUNTRY_OPTIONS.map((option) => (
              <option key={option.country} value={option.country}>
                {option.shortLabel}
              </option>
            ))}
          </select>
        </div>

        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          value={nationalNumber}
          placeholder={placeholder}
          style={inputStyle}
          onChange={(event) => {
            const nextValue = String(event.target.value || "").replace(/\D/g, "").slice(0, maxNationalNumberLength);
            const nextStoredValue = formatStoredPhoneValue(selectedCountry, nextValue);
            setNationalNumber(nextValue);
            onChange?.(nextStoredValue);
          }}
          maxLength={maxNationalNumberLength}
        />
      </div>

      {showDigitCount ? <div style={styles.digitCount}>{digitCountLabel}</div> : null}
    </div>
  );
}

function buildPhoneCountryOptions() {
  return getCountries()
    .map((country) => {
      const callingCode = safeGetCountryCallingCode(country);
      const countryName = REGION_DISPLAY_NAMES?.of(country) || country;
      return {
        country,
        countryName,
        callingCode,
        dialCode: `+${callingCode}`,
        shortLabel: `${country} +${callingCode}`,
      };
    })
    .filter((option) => option.callingCode)
    .sort((left, right) => {
      if (left.country === DEFAULT_COUNTRY) return -1;
      if (right.country === DEFAULT_COUNTRY) return 1;
      return left.countryName.localeCompare(right.countryName) || left.country.localeCompare(right.country);
    });
}

function safeGetCountryCallingCode(country) {
  try {
    return getCountryCallingCode(country);
  } catch {
    return "";
  }
}

function getCountryOption(country) {
  return PHONE_COUNTRY_OPTION_BY_CODE.get(country) || PHONE_COUNTRY_OPTION_BY_CODE.get(DEFAULT_COUNTRY) || {
    country: DEFAULT_COUNTRY,
    countryName: DEFAULT_COUNTRY,
    callingCode: "91",
    dialCode: "+91",
    shortLabel: "IN +91",
  };
}

function parseStoredPhoneParts(value, fallbackCountry = DEFAULT_COUNTRY, options = {}) {
  const fallbackOption = getCountryOption(fallbackCountry);
  const { inferCountryFromValue = false, lockCountryToFallback = false } = options;
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return {
      country: fallbackOption.country,
      callingCode: fallbackOption.callingCode,
      nationalNumber: "",
      display: "",
    };
  }

  const hasExplicitInternationalPrefix = rawValue.startsWith("+");
  const digits = rawValue.replace(/\D/g, "");

  if (!hasExplicitInternationalPrefix) {
    if (inferCountryFromValue && !lockCountryToFallback && digits.length > getCountryNationalNumberMaxLength(fallbackOption.country)) {
      const parsedInternationalDigits = parsePhoneNumberFromString(`+${digits}`);
      if (parsedInternationalDigits?.countryCallingCode && parsedInternationalDigits?.nationalNumber) {
        const parsedCountry = parsedInternationalDigits.country || getCountryByCallingCode(parsedInternationalDigits.countryCallingCode);
        const country = parsedCountry || fallbackOption.country;
        const option = getCountryOption(country);
        return {
          country,
          callingCode: String(parsedInternationalDigits.countryCallingCode || option.callingCode),
          nationalNumber: parsedInternationalDigits.nationalNumber || "",
          display: formatStoredPhoneValue(country, parsedInternationalDigits.nationalNumber || ""),
          shortLabel: option.shortLabel,
        };
      }
    }

    const nationalNumber = stripDefaultCountryCode(fallbackOption.country, digits, fallbackOption.callingCode);
    return {
      country: fallbackOption.country,
      callingCode: fallbackOption.callingCode,
      nationalNumber,
      display: formatStoredPhoneValue(fallbackOption.country, nationalNumber),
    };
  }

  const parsedRaw = parsePhoneNumberFromString(rawValue, fallbackOption.country);
  if (parsedRaw?.countryCallingCode) {
    const parsedCallingCode = String(parsedRaw.countryCallingCode || "");
    const parsedCountry = parsedRaw.country || getCountryByCallingCode(parsedCallingCode);
    const country = inferCountryFromValue && !lockCountryToFallback
      ? parsedCountry || fallbackOption.country
      : fallbackOption.country;
    const option = getCountryOption(country);
    return {
      country,
      callingCode: parsedCallingCode,
      nationalNumber: parsedRaw.nationalNumber || "",
      display: formatStoredPhoneValue(country, parsedRaw.nationalNumber || ""),
      shortLabel: option.shortLabel,
    };
  }

  if (!digits) {
    return {
      country: fallbackOption.country,
      callingCode: fallbackOption.callingCode,
      nationalNumber: "",
      display: "",
    };
  }

  const prefixOption = getCountryByCallingCodePrefix(digits);
  if (prefixOption?.callingCode) {
    const country = inferCountryFromValue && !lockCountryToFallback
      ? prefixOption.country
      : fallbackOption.country;
    const option = getCountryOption(country);
    const nationalNumber = digits.slice(prefixOption.callingCode.length);
    return {
      country,
      callingCode: prefixOption.callingCode,
      nationalNumber,
      display: formatStoredPhoneValue(country, nationalNumber),
      shortLabel: option.shortLabel,
    };
  }

  const parsedDigits = parsePhoneNumberFromString(`+${digits}`);
  if (parsedDigits?.countryCallingCode) {
    const parsedCallingCode = String(parsedDigits.countryCallingCode || "");
    const parsedCountry = parsedDigits.country || getCountryByCallingCode(parsedCallingCode);
    const country = inferCountryFromValue && !lockCountryToFallback
      ? parsedCountry || fallbackOption.country
      : fallbackOption.country;
    return {
      country,
      callingCode: parsedCallingCode,
      nationalNumber: parsedDigits.nationalNumber || "",
      display: formatStoredPhoneValue(country, parsedDigits.nationalNumber || ""),
    };
  }

  const fallbackNationalNumber = stripDefaultCountryCode(fallbackOption.country, digits, fallbackOption.callingCode);
  return {
    country: fallbackOption.country,
    callingCode: fallbackOption.callingCode,
    nationalNumber: fallbackNationalNumber,
    display: formatStoredPhoneValue(fallbackOption.country, fallbackNationalNumber),
  };
}

function stripDefaultCountryCode(country, digits, callingCode) {
  if (country === "IN") {
    if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
    if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
  }

  if (digits.startsWith(callingCode) && digits.length > callingCode.length) {
    return digits.slice(callingCode.length);
  }

  return digits;
}

function formatStoredPhoneValue(country, nationalNumber) {
  const digits = String(nationalNumber || "").replace(/\D/g, "");
  if (!digits) return "";
  const option = getCountryOption(country);
  return `${option.dialCode} ${digits}`.trim();
}

function getCountryByCallingCode(callingCode) {
  const normalizedCallingCode = String(callingCode || "").replace(/\D/g, "");
  if (!normalizedCallingCode) return "";
  return PHONE_COUNTRY_OPTIONS.find((option) => option.callingCode === normalizedCallingCode)?.country || "";
}

function getCountryByCallingCodePrefix(digits) {
  const normalizedDigits = String(digits || "").replace(/\D/g, "");
  if (!normalizedDigits) return null;
  return [...PHONE_COUNTRY_OPTIONS]
    .sort((left, right) => right.callingCode.length - left.callingCode.length)
    .find((option) => normalizedDigits.startsWith(option.callingCode)) || null;
}

function getCountryNationalNumberMaxLength(country) {
  const possibleLengths = getCountryNationalNumberPossibleLengths(country);
  return Math.max(...possibleLengths);
}

function getCountryNationalNumberPossibleLengths(country) {
  const normalizedCountry = String(country || DEFAULT_COUNTRY).toUpperCase();
  if (PHONE_COUNTRY_MAX_LENGTH_CACHE.has(normalizedCountry)) {
    return PHONE_COUNTRY_MAX_LENGTH_CACHE.get(normalizedCountry);
  }

  const countryData = mobilePhoneMetadata?.countries?.[normalizedCountry];
  const mobileTypeData = Array.isArray(countryData?.[11]) ? countryData[11][1] : null;
  const mobilePossibleLengths = Array.isArray(mobileTypeData?.[1]) ? mobileTypeData[1] : [];
  const generalPossibleLengths = Array.isArray(countryData?.[3]) ? countryData[3] : [];
  const possibleLengths = (mobilePossibleLengths.length ? mobilePossibleLengths : generalPossibleLengths)
    .filter((length) => Number.isFinite(length));
  const lengths = possibleLengths.length ? [...new Set(possibleLengths)].sort((a, b) => a - b) : [15];

  PHONE_COUNTRY_MAX_LENGTH_CACHE.set(normalizedCountry, lengths);
  return lengths;
}

function formatPossibleLengthLabel(lengths) {
  const safeLengths = Array.isArray(lengths) && lengths.length ? lengths : [15];
  if (safeLengths.length === 1) return `${safeLengths[0]}-digit`;
  return `${safeLengths.join(" or ")}-digit`;
}

const styles = {
  digitCount: {
    justifySelf: "end",
    fontSize: 12,
    color: "#7f8795",
    paddingRight: 2,
  },
};
